// Núcleo de cálculo del modelo de calendario de Objetivos/Metas (spec v6).
// Puerto literal de las funciones puras del mockup de referencia
// (sustemia_objetivos_matriz_estrategia.html) — mismo algoritmo, sin cambios.
// Fechas siempre como string ISO 'YYYY-MM-DD', comparadas lexicográficamente
// (nunca como Date, salvo dentro de genOcurrencias para la aritmética de
// generación). Nada de esto se persiste: "avance" y "vencida" se recalculan
// siempre a partir de las ocurrencias que trae la meta.

const PASO_DIAS = { Semanal: 7, Quincenal: 15 };
const PASO_MESES = { Mensual: 1, Bimestral: 2, Trimestral: 3, Semestral: 6, Anual: 12 };
const MAX_OCURRENCIAS = 130;

function toIso(d) {
  if (!d) return null;
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return String(d).slice(0, 10);
}

export function genOcurrencias(ini, fin, frecuencia) {
  const out = [];
  if (!ini) return out;
  const iniIso = toIso(ini);
  if (frecuencia === "Única") {
    out.push({ fecha: iniIso, cumplida: false });
    return out;
  }
  const pasoDias = PASO_DIAS[frecuencia];
  const pasoMeses = PASO_MESES[frecuencia];
  let d = new Date(iniIso + "T00:00:00");
  const end = new Date(toIso(fin || ini) + "T00:00:00");
  let guard = 0;
  while (d <= end && guard < MAX_OCURRENCIAS) {
    out.push({ fecha: d.toISOString().slice(0, 10), cumplida: false });
    if (pasoDias) d = new Date(d.getTime() + pasoDias * 86400000);
    else {
      d = new Date(d);
      d.setMonth(d.getMonth() + pasoMeses);
    }
    guard++;
  }
  return out;
}

// Normaliza las ocurrencias que vienen de Mongo (fecha como Date, cumplida
// como boolean) al formato de trabajo del mockup (fecha ISO string).
export function normalizeOcurrencias(ocurrencias) {
  return (ocurrencias || []).map((o) => ({
    _id: o._id,
    fecha: toIso(o.fecha),
    cumplida: !!o.cumplida,
  }));
}

export function avanceMeta(m) {
  const oc = m.ocurrencias || [];
  if (!oc.length) return 0;
  return (oc.filter((x) => x.cumplida).length / oc.length) * 100;
}

export function vencidasMeta(m, hoyIso) {
  const hoy = hoyIso || toIso(new Date());
  return (m.ocurrencias || []).filter((x) => !x.cumplida && x.fecha < hoy).length;
}

export function estadoMeta(m, hoyIso) {
  const av = avanceMeta(m);
  const venc = vencidasMeta(m, hoyIso);
  if (av >= 100) return { cls: "ec-ok", txt: "✔ Cumplida" };
  if (venc > 0) return { cls: "ec-late", txt: `⚠ ${venc} vencida${venc === 1 ? "" : "s"}` };
  if (av > 0) return { cls: "ec-warn", txt: "◐ En curso" };
  return { cls: "ec-none", txt: "○ Pendiente" };
}

// avance de un objetivo = promedio simple de avanceMeta de sus metas (0..1)
export function avanceDe(o) {
  const ms = o.metas || [];
  if (!ms.length) return 0;
  return Math.max(0, Math.min(1, ms.reduce((s, m) => s + avanceMeta(m), 0) / ms.length / 100));
}

export function esperadoMetaHasta(m, corteIso) {
  const oc = m.ocurrencias || [];
  if (!oc.length) return 0;
  return oc.filter((x) => x.fecha <= corteIso).length / oc.length;
}

export function esperadoHasta(o, corteIso) {
  const ms = o.metas || [];
  if (!ms.length) return 0;
  return ms.reduce((s, m) => s + esperadoMetaHasta(m, corteIso), 0) / ms.length;
}

export function esperadoDe(o, hoyIso) {
  return esperadoHasta(o, hoyIso || toIso(new Date()));
}

// Ponderado por "peso" (reductionTons/pct del objetivo) — mismo patrón que
// usa el mockup para avance_global y avance por aspecto: Σ(avance_i×peso_i)/Σpeso_i.
export function avancePonderado(objetivos, avanceFn, pesoKey = "reductionTons") {
  let pond = 0;
  let peso = 0;
  (objetivos || []).forEach((o) => {
    const p = o[pesoKey] || 0;
    pond += avanceFn(o) * p;
    peso += p;
  });
  return peso ? pond / peso : 0;
}

// Cumplimiento anual de metas (v6.5): cuenta, entre TODAS las ocurrencias de
// TODOS los objetivos, cuántas caen en el año dado y cuántas de esas ya están
// cumplidas — para la tabla "Cumplimiento anual de metas" del seguimiento.
export function cumplimientoAnual(objetivos, year) {
  let cumplidas = 0;
  let programadas = 0;
  (objetivos || []).forEach((o) => {
    (o.metas || []).forEach((m) => {
      (m.ocurrencias || []).forEach((oc) => {
        if (String(oc.fecha).slice(0, 4) === String(year)) {
          programadas++;
          if (oc.cumplida) cumplidas++;
        }
      });
    });
  });
  return { cumplidas, programadas };
}

// Cumplimiento anual de UNA meta (v6.5, tabla por objetivo × año): a
// diferencia de cumplimientoAnual (que agrega TODAS las metas de golpe, para
// la fila "PLAN"), esto es lo que necesita cada celda meta×año de la tabla.
export function cumplimientoAnualMeta(m, year, hoyIso) {
  const hoy = hoyIso || toIso(new Date());
  let cumplidas = 0;
  let programadas = 0;
  let vencidas = 0;
  (m.ocurrencias || []).forEach((oc) => {
    if (String(oc.fecha).slice(0, 4) !== String(year)) return;
    programadas++;
    if (oc.cumplida) cumplidas++;
    else if (oc.fecha < hoy) vencidas++;
  });
  return { cumplidas, programadas, vencidas };
}

export { toIso, MAX_OCURRENCIAS };
